import React, { useState } from 'react';
import { ChatPage } from './pages/ChatPage';
import { HistoryPage } from './pages/HistoryPage';
import { ActivitiesPage } from './pages/ActivitiesPage';
import { SettingsPage } from './pages/SettingsPage';
import { FamigliaPage } from './pages/FamigliaPage';
import { ChildDetailPage } from './pages/ChildDetailPage';
import { CaregiverDetailPage } from './pages/CaregiverDetailPage';
import { WelcomePage } from './pages/WelcomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { HomePage } from './pages/HomePage';
import { AppLayout } from './components/AppLayout';
import { TopButtons } from './components/TopButtons';
import { YeppyChatMode } from './components/EmptyChatSuggestions';
export type PageType = 'home' | 'chat' | 'history' | 'activities' | 'settings' | 'family' | 'child-detail' | 'caregiver-detail';
type AuthView = 'welcome' | 'login' | 'register';
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [authView, setAuthView] = useState<AuthView>('welcome');
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [selectedChildName, setSelectedChildName] = useState<string>('');
  const [selectedCaregiverId, setSelectedCaregiverId] = useState<string | null>(null);
  const [selectedCaregiverName, setSelectedCaregiverName] = useState<string>('');
  const [chatTitle, setChatTitle] = useState('Nuova chat');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [chatMode, setChatMode] = useState<YeppyChatMode>('attivita');
  const [hasMessages, setHasMessages] = useState(false);
  const [chatEntryAnimation, setChatEntryAnimation] = useState(false);
  const [chatInitialMessage, setChatInitialMessage] = useState<string | null>(null);
  const toggleMenu = () => setIsMenuOpen(prev => !prev);
  const closeMenu = () => setIsMenuOpen(false);
  const handleLogin = () => {
    setIsAuthenticated(true);
    setHasCompletedOnboarding(true);
  };
  const handleRegister = () => {
    setIsAuthenticated(true);
    setHasCompletedOnboarding(false);
  };
  const handleOnboardingComplete = () => {
    setHasCompletedOnboarding(true);
  };
  const handleLogout = () => {
    setIsAuthenticated(false);
    setHasCompletedOnboarding(false);
    setCurrentPage('home');
    setSelectedConversationId(null);
  };
  const handleOpenConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setCurrentPage('chat');
  };
  const handleNewConversation = (mode?: YeppyChatMode) => {
    setSelectedConversationId(null);
    setCurrentPage('chat');
    setChatTitle('Nuova chat');
    setHasMessages(false);
    if (mode) {
      setChatMode(mode);
      setChatEntryAnimation(true);
      // Set initial Yeppy message based on mode
      const modeMessages: Record<string, string> = {
        ascolto: 'Bene! Aggiungi informazioni sui bambini, le registrerò! 📝',
        informazioni: 'Ciao! Chiedimi qualsiasi cosa sui tuoi bambini, cercherò nelle info salvate 🔍',
        attivita: 'Perfetto! Dimmi cosa devi organizzare e creerò una lista di attività per te ✅'
      };
      setChatInitialMessage(modeMessages[mode] || null);
    } else {
      setChatInitialMessage(null);
    }
  };
  const handleBackToHistory = () => {
    setCurrentPage('history');
  };
  const handleOpenChild = (childId: string, childName?: string) => {
    setSelectedChildId(childId);
    // If name not provided, we could look it up, but for now we'll just use a fallback or expect it to be passed
    setSelectedChildName(childName || 'Bambino');
    setCurrentPage('child-detail');
  };
  const handleOpenCaregiver = (caregiverId: string, caregiverName?: string) => {
    setSelectedCaregiverId(caregiverId);
    setSelectedCaregiverName(caregiverName || 'Caregiver');
    setCurrentPage('caregiver-detail');
  };
  const handleFamilyClick = () => {
    setCurrentPage('family');
  };
  // Show auth pages if not authenticated
  if (!isAuthenticated) {
    if (authView === 'welcome') {
      return <WelcomePage onGoToLogin={() => setAuthView('login')} onGoToRegister={() => setAuthView('register')} data-id="element-0" />;
    } else if (authView === 'login') {
      return <LoginPage onLogin={handleLogin} onSwitchToRegister={() => setAuthView('register')} onBack={() => setAuthView('welcome')} data-id="element-1" />;
    } else {
      return <RegisterPage onRegister={handleRegister} onSwitchToLogin={() => setAuthView('login')} onBack={() => setAuthView('welcome')} data-id="element-2" />;
    }
  }
  // Show onboarding if authenticated but hasn't completed onboarding
  if (!hasCompletedOnboarding) {
    return <OnboardingPage onComplete={handleOnboardingComplete} data-id="element-3" />;
  }
  // Show main app if authenticated and onboarding completed
  return <AppLayout currentPage={currentPage} onNavigate={setCurrentPage} onNewConversation={handleNewConversation} onLogout={handleLogout} isMenuOpen={isMenuOpen} onMenuClose={closeMenu} data-id="element-4">
      {currentPage === 'home' ? <div className="flex flex-col h-full w-full" data-id="element-5">
          <TopButtons onMenuClick={toggleMenu} onFamilyClick={handleFamilyClick} pageTitle="Home" data-id="element-6" />
          <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-7">
            <HomePage onNewChat={handleNewConversation} onOpenChild={handleOpenChild} onOpenActivities={() => setCurrentPage('activities')} onOpenFamily={() => setCurrentPage('family')} data-id="element-8" />
          </div>
        </div> : currentPage === 'chat' ? <div className={`flex flex-col h-full w-full ${chatEntryAnimation ? 'animate-slide-up-page' : ''}`} onAnimationEnd={() => setChatEntryAnimation(false)} data-id="element-9">
          <TopButtons onMenuClick={toggleMenu} onFamilyClick={handleFamilyClick} showBackButton={true} onBackClick={selectedConversationId ? handleBackToHistory : () => setCurrentPage('home')} backLabel={selectedConversationId ? 'Storico' : 'Home'} pageTitle={selectedConversationId ? chatTitle : 'Chat'} chatMode={!selectedConversationId ? chatMode : undefined} onModeChange={setChatMode} hasMessages={hasMessages} data-id="element-10" />

          <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-11">
            <ChatPage conversationId={selectedConversationId} onNewConversation={handleNewConversation} onBackToHistory={handleBackToHistory} onChatTitleChange={setChatTitle} onOpenConversation={handleOpenConversation} chatMode={chatMode} onModeChange={setChatMode} onHasMessagesChange={setHasMessages} initialMessage={chatInitialMessage} onInitialMessageConsumed={() => setChatInitialMessage(null)} onOpenChild={handleOpenChild} data-id="element-12" />
          </div>
        </div> : currentPage === 'history' ? <div className="flex flex-col h-full w-full" data-id="element-13">
          <TopButtons onMenuClick={toggleMenu} onFamilyClick={handleFamilyClick} showBackButton={true} onBackClick={() => setCurrentPage('home')} backLabel="Home" pageTitle="Storico" data-id="element-14" />
          <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-15">
            <HistoryPage onOpenConversation={handleOpenConversation} data-id="element-16" />
          </div>
        </div> : currentPage === 'activities' ? <div className="flex flex-col h-full w-full" data-id="element-17">
          <TopButtons onMenuClick={toggleMenu} onFamilyClick={handleFamilyClick} showBackButton={true} onBackClick={() => setCurrentPage('home')} backLabel="Home" pageTitle="Attività" data-id="element-18" />
          <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-19">
            <ActivitiesPage data-id="element-20" />
          </div>
        </div> : currentPage === 'settings' ? <div className="flex flex-col h-full w-full" data-id="element-21">
          <TopButtons onMenuClick={toggleMenu} onFamilyClick={handleFamilyClick} showBackButton={true} onBackClick={() => setCurrentPage('home')} backLabel="Home" pageTitle="Impostazioni" data-id="element-22" />
          <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-23">
            <SettingsPage onMenuOpen={toggleMenu} data-id="element-24" />
          </div>
        </div> : currentPage === 'family' ? <div className="flex flex-col h-full w-full" data-id="element-25">
          <TopButtons onMenuClick={toggleMenu} onFamilyClick={handleFamilyClick} showBackButton={true} onBackClick={() => setCurrentPage('home')} backLabel="Home" pageTitle="Nucleo Familiare" data-id="element-26" />
          <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-27">
            <FamigliaPage onOpenChild={(id, name) => handleOpenChild(id, name)} onOpenActivities={() => setCurrentPage('activities')} onOpenCaregiver={(id, name) => handleOpenCaregiver(id, name)} onOpenStats={() => {}} data-id="element-28" />
          </div>
        </div> : currentPage === 'child-detail' && selectedChildId ? <div className="flex flex-col h-full w-full animate-push-in" data-id="element-29">
          <TopButtons onMenuClick={toggleMenu} onFamilyClick={handleFamilyClick} showBackButton={true} onBackClick={() => setCurrentPage('family')} backLabel="Famiglia" hideRightButton={true} pageTitle={selectedChildName} data-id="element-30" />
          <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-31">
            <ChildDetailPage childId={selectedChildId} onAskYeppy={childName => {
          setSelectedConversationId(null);
          setChatTitle(`Info su ${childName}`);
          setChatMode('informazioni');
          setHasMessages(false);
          setChatEntryAnimation(true);
          setCurrentPage('chat');
        }} data-id="element-32" />
          </div>
        </div> : currentPage === 'caregiver-detail' && selectedCaregiverId ? <div className="flex flex-col h-full w-full animate-push-in" data-id="element-33">
          <TopButtons onMenuClick={toggleMenu} onFamilyClick={handleFamilyClick} showBackButton={true} onBackClick={() => setCurrentPage('family')} backLabel="Famiglia" hideRightButton={true} pageTitle={selectedCaregiverName} data-id="element-34" />
          <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-35">
            <CaregiverDetailPage caregiverId={selectedCaregiverId} data-id="element-36" />
          </div>
        </div> : null}
    </AppLayout>;
}